import { Button } from "./button";
import { ShoppingCart, PackageCheckIcon } from "lucide-react";
import { useCart } from "@/lib/hooks/useCart";
import { ProductImageFallback } from "./ProductImageFallback";
import {
  getDisplayPricing,
  renderCardPrices,
  type DisplayPricing,
} from "@/lib/utils/generatePrice";
import { type Product, type Price } from "@/lib/models/product";
import { useMemo, useState } from "react";
import { useSession } from "next-auth/react";
import { Badge } from "./badge";
import { CustomTooltip } from "./tooltip";
import { useModal } from "@/lib/hooks/useModal";
import { Stock } from "@/app/productos/Stock";
import { filterBrand } from "@/lib/utils/RedirectBrand";
import {
  canSupplierViewBrand,
  canViewPrivilegedPrices,
  isAdminSession,
  isInternalSession,
  isSapLinkedSession,
  isSupplierSession,
  supplierPriceMessage,
  defaultPriceInvitationMessage,
} from "@/lib/utils/pricingVisibility";
import Link from "next/link";
import { IntegratorNotice } from "./IntegratorNotice";
import { QuantitySelector } from "./QuantitySelector";
import { PriceRangeModalContent } from "./PriceRangeTable";

interface ProductCardHorizontalProps {
  product: Product;
  closeSuggestion?: () => void;
}

export const ProductCardHorizontal = ({
  product,
  closeSuggestion,
}: ProductCardHorizontalProps) => {
  const { data: session } = useSession();
  const { addItemToCart, productKey, formatPrice } = useCart();
  const { showModal, closeTopModal } = useModal();
  const [quantity, setQuantity] = useState(1);

  const hasPriceRanges = useMemo(
    () =>
      Boolean(
        product.price?.secciones?.some(
          (s) =>
            (s.tipo === "PRECIOS_POR_PERIODO_Y_CANTIDAD_RANGOS" ||
              s.tipo === "PRECIOS_POR_PERIODO_Y_CANTIDAD_SIN_RANGOS") &&
            (s.entradas?.length ?? 0) > 0,
        ),
      ),
    [product.price?.secciones],
  );

  const isAdmin = isAdminSession(session);
  const isLinked = isSapLinkedSession(session);
  const isSupplier = isSupplierSession(session);
  const isInternal = isInternalSession(session);
  const canViewSupplierBrand = canSupplierViewBrand(session, product.marcaCode);
  const canViewPrivilegedPrice = canViewPrivilegedPrices(
    session,
    product.marcaCode,
  );
  const canAccessProductActions =
    !isSupplier &&
    (canViewPrivilegedPrice || product.itemVentaDirecta !== false);
  const canViewStock =
    isAdmin ||
    isInternal ||
    isLinked ||
    isSupplier ||
    product.itemVentaDirecta !== false;
  const isDefaultCustomer =
    session === null || session === undefined || !canViewPrivilegedPrice;
  // Aviso de precios de integrador (invitados y clientes no vinculados), solo
  // en productos que no son de venta directa; `undefined` es la sesión aún
  // cargando.
  const showIntegratorNotice =
    session !== undefined &&
    !canViewPrivilegedPrice &&
    !isSupplier &&
    product.itemVentaDirecta === false;

  const displayPricing = useMemo(
    () => getDisplayPricing(product, isDefaultCustomer, productKey) || {},
    [product, isDefaultCustomer, productKey],
  );

  function showPrice() {
    if (canViewPrivilegedPrice) {
      return (
        <div className="flex gap-2 items-center flex-wrap w-full">
          {renderCardPrices(
            product,
            isDefaultCustomer,
            productKey,
            formatPrice,
            "text-base sm:text-lg",
          )}
        </div>
      );
    }

    if (isSupplier) {
      return (
        <span className="text-xs text-primary font-medium italic leading-relaxed hover:text-secondary group-hover:text-secondary transition-colors duration-200">
          {supplierPriceMessage}
        </span>
      );
    }

    // 2. Si el producto NO es de venta directa, mostramos mensaje de invitación
    if (product.itemVentaDirecta === false) {
      return (
        <span className="text-xs text-primary font-medium italic leading-relaxed hover:text-secondary group-hover:text-secondary transition-colors duration-200">
          {defaultPriceInvitationMessage}
        </span>
      );
    }

    // 3. Producto de venta directa para usuarios no vinculados o invitados
    if (product.itemVentaDirecta) {
      return (
        <div className="flex gap-2 items-center flex-wrap w-full">
          {renderCardPrices(
            product,
            isDefaultCustomer,
            productKey,
            formatPrice,
            "text-base sm:text-lg",
          )}
        </div>
      );
    }

    // 4. Fallback si no hay sesión y no es venta directa
    if (session === null) {
      return (
        <span className="text-xs text-primary font-medium italic leading-relaxed hover:text-secondary group-hover:text-secondary transition-colors duration-200">
          {defaultPriceInvitationMessage}
        </span>
      );
    }

    return null;
  }

  const totalStock =
    product.price?.stockDisponibleTotal ??
    product.stockDisponibleTotal ??
    product.stockTotal ??
    0;

  const handleOpenPriceRanges = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    showModal({
      title: "Precios por volumen",
      content: (
        <PriceRangeModalContent
          product={product}
          initialQuantity={quantity}
          totalStock={totalStock}
          onAddToCart={(finalQty) => {
            setQuantity(finalQty);
            const pricing = displayPricing as DisplayPricing;
            const unitPrice = pricing?.mainPrice || product?.priceList1 || 0;
            addItemToCart(product, unitPrice, finalQty);
            if (closeSuggestion) {
              closeSuggestion();
            }
            closeTopModal();
          }}
        />
      ),
      size: "xl",
    });
  };

  return (
    <Link
      href={`/producto/${product.slug}`}
      onClick={(e) => {
        const target = e.target as Element;
        const innerLink = target.closest("a");
        const isInnerLink = innerLink && innerLink !== e.currentTarget;
        const isInteractive =
          target.closest("button") ||
          target.closest(".no-navigate") ||
          target.closest("[data-no-navigate]") ||
          isInnerLink;

        if (isInteractive || e.defaultPrevented) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }

        closeTopModal();
        if (closeSuggestion) {
          closeSuggestion();
        }
      }}
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-300 w-full grid grid-cols-[8rem_minmax(0,1fr)] gap-4 p-4 border border-gray-200 group"
    >
      {/* Image Container - Left Side */}
      <div className="relative bg-gray-50 flex-shrink-0 w-32 h-32 rounded flex items-center justify-center overflow-hidden row-span-1 sm:row-span-1 md:row-span-1 lg:row-span-2 xl:row-span-2 2xl:row-span-2">
        {product.isNew && (
          <div className="absolute top-2 left-2 z-20">
            <Badge variant="success" className="text-[10px]">
              Nuevo
            </Badge>
          </div>
        )}
        <ProductImageFallback
          urlMainImg={product?.urlMainImg}
          alt={product?.name || ""}
          className="w-full h-full object-contain p-2"
          iconSize={48}
        />
      </div>

      {/* Content - Right Side */}
      <div className="flex flex-col flex-1 min-w-0 justify-between">
        {/* Top: Title, SKU, Marca */}
        <div className="flex-1">
          {/* Title - line clamp para 2-3 líneas */}
          <div className="flex items-center justify-between">
            {product.name && product.name.length > 20 ? (
              <CustomTooltip content={product.name}>
                <span className="text-sm sm:text-base text-gray-900 font-semibold line-clamp-2 leading-snug mb-1">
                  {product.name}
                </span>
              </CustomTooltip>
            ) : (
              <span className="text-sm sm:text-base text-gray-900 font-semibold line-clamp-2 leading-snug mb-1">
                {product.name}
              </span>
            )}

            {canViewStock && (
              <Button
                variant="ghostPrimary"
                size="sm"
                type="button"
                className="flex items-center p-2 no-navigate"
                tooltip={`Stock disponible: ${totalStock}`}
                onClick={(e) => {
                  e.stopPropagation();
                  showModal({
                    title: `Disponibilidad: ${product.sku}`,
                    content: <Stock product={product} />,
                    size: "md",
                  });
                }}
              >
                <PackageCheckIcon className=" size-4" />
              </Button>
            )}
          </div>
          {/* SKU */}
          <span className="text-xs text-gray-500 block mb-2">
            {product.sku} |{" "}
            {product.marcaName && (
              <span
                className="text-primary cursor-pointer hover:text-primary/50"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  filterBrand(product.marcaCode);
                }}
              >
                {product.marcaName}
              </span>
            )}
          </span>

          {canViewStock && (
            <p className="text-md font-semibold text-gray-500 col-span-6">
              Stock disponible: {totalStock}
            </p>
          )}
        </div>

        {/* Bottom: Price and Stock Button */}
        {!showIntegratorNotice && (
          <div className="flex items-center justify-between pt-3 border-t border-gray-200 gap-2">
            {/* Price & Badge */}
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {showPrice()}
            </div>
            {canAccessProductActions && (
              <div className="flex items-center gap-2">
                <QuantitySelector
                  quantity={quantity}
                  min={1}
                  max={totalStock > 0 ? totalStock : 1}
                  onChange={setQuantity}
                  disabled={totalStock <= 0}
                  className="w-28 sm:w-32"
                />
                <Button
                  size="sm"
                  type="button"
                  className="flex items-center gap-1.5 whitespace-nowrap no-navigate h-8 px-2.5 sm:px-3 text-xs shrink-0"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (hasPriceRanges) {
                      handleOpenPriceRanges(e);
                      return;
                    }

                    const pricing = displayPricing as DisplayPricing;
                    const unitPrice =
                      pricing?.mainPrice || product?.priceList1 || 0;
                    addItemToCart(product, unitPrice, quantity);
                    if (closeSuggestion) {
                      closeSuggestion();
                    }
                    closeTopModal();
                  }}
                  disabled={totalStock <= 0}
                >
                  <ShoppingCart size={16} />
                  <span className="hidden sm:inline text-xs">Agregar</span>
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Aviso de integrador: a todo el ancho de la card hasta lg; desde lg en
          la columna del contenido */}
      {showIntegratorNotice && (
        <div className="col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 lg:col-start-2 xl:col-span-1 xl:col-start-2 2xl:col-span-1 2xl:col-start-2">
          <IntegratorNotice
            isGuest={session === null}
            insideLink
            compact
            onNavigate={() => {
              closeTopModal();
              closeSuggestion?.();
            }}
          />
        </div>
      )}
    </Link>
  );
};
